// @flow
import type { SampleList } from "../../lib/OnlineTypes";
import React, { useState, Fragment } from "react";
import Samples from "./Samples";
import ProductFailureSampleSearch from "./ProductFailureSampleSearch";
import { DefaultPaddedContainerNoShadow } from "./Theme";
import ProductFailureReport from "./ProductFailureReport";
import { fetchSamples } from "./Utils/SamplesFetch";
import moment from "moment";
import DropDown from "../DropDown";
import styled from "styled-components";

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

const Container = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  margin-top: 20px;
`;

function splitBy(dates, formatCode) {
  const groups = [];
  var currentGroup = null;
  dates.forEach(date => {
    const groupBy = moment.utc(date).format(formatCode);
    if (
      currentGroup == null ||
      (currentGroup.length > 0 && moment.utc(currentGroup[0]).format(formatCode) !== groupBy)
    ) {
      currentGroup = [];
      groups.push(currentGroup);
    }
    currentGroup.push(date);
  });
  return groups;
}

function splitDates(groupBy, dates) {
  if (groupBy === "default") {
    // Don't group dates if the dates are less than 28 days or if they are in just one month
    const monthCount = dates
      .map(date =>
        moment
          .utc(date)
          .local()
          .format("M")
      )
      .filter(onlyUnique).length;
    if (dates.length <= 28 || monthCount == 1) {
      return [dates];
    }
    return splitBy(dates, "M");
  } else if (groupBy === "month") {
    return splitBy(dates, "M");
  } else if (groupBy === "week") {
    return splitBy(dates, "w");
  } else if (groupBy === "week_iso") {
    return splitBy(dates, "W");
  }
}

export default function ProductFailureSummary({
  company_id,
  canFindCompany,
  runSearch = false,
  clientView = true
}) {
  const [failureData, setFailureData] = useState(null);
  const [sampleList, setSampleList] = useState(null);
  const [groupBy, setGroupBy] = useState("default");
  const [loadingSamples, setLoadingSamples] = useState(false);

  const findSamples = ({ product_id, date, attributes }) => {
    setLoadingSamples(true);
    const data_field_params = attributes
      .filter(a => a.type === "data_field_name")
      .map(a => ({
        match_type: "exactly",
        data_field_name_id: a.id,
        text_search: a.value
      }));
    const clientSuiteAttribute = attributes.filter(a => a.type === "client_suite");
    const client_suite_id =
      clientSuiteAttribute.length > 0 ? clientSuiteAttribute[0].id : null;

    fetchSamples({
      client_suite_id,
      company_id,
      product_id,
      start_date: moment.utc(date),
      end_date: moment.utc(date),
      data_field_params
    }).then(sampleList => {
      setLoadingSamples(false);
      setSampleList(null);
      setSampleList(sampleList);
    });
  };

  return (
    <div>
      <ProductFailureSampleSearch
        company_id={company_id}
        canFindCompany={false}
        onLoad={setFailureData}
        runSearch={runSearch}
      />
      {sampleList && (
        <div style={{ marginTop: 20, opacity: loadingSamples ? 0.5 : 1 }}>
          <Samples
            sample_list={sampleList}
            company_id={company_id}
            showAttributes={false}
            clientView={clientView}
          />
        </div>
      )}
      {failureData && (
        <Fragment>
          <Container>
            <DropDown
              options={[
                { label: I18n.t("standard_grouping"), value: "default" },
                { label: I18n.t("group_by_month"), value: "month" },
                { label: I18n.t("group_by_week_of_year_sun_sat"), value: "week" },
                { label: I18n.t("group_by_week_of_year_mon_sun"), value: "week_iso" }
              ]}
              value={groupBy}
              onChange={item => setGroupBy(item.value)}
            />
          </Container>
          {splitDates(groupBy, failureData.dates).map((dates, i) => (
            <ProductFailureReport
              key={i}
              data={failureData}
              findSamples={findSamples}
              dates={dates}
            />
          ))}
        </Fragment>
      )}
    </div>
  );
}
